/**
 * Pilotage-plan corner chips, shown whenever a plan is loaded/active (building or
 * just viewing). Two small, unobtrusive chips that live in the chart chip-stack,
 * not modals:
 *
 *  - PlanVisibilityChip: a single tap shows or hides the WHOLE plan on the chart.
 *  - TideHeightChip: the live height of tide for the reference port, so you can
 *    cross-check a charted depth against the echo sounder as you cross a contour.
 *    It reads the same tide-height prediction the rest of the app uses, at the
 *    app's current tide instant, so it tracks the scrubber in real time.
 */
import { usePilotagePlannerStore, selectActivePlan } from '@/store/pilotagePlanner';
import { useShellStore } from '@/store/shell';
import { useEnvStore } from '@/store/environment';
import { useChartPlotterStore } from '@/store';
import { useTideSourceStore } from '@/store/tideSources';
import { useUnitsStore, formatDepth } from '@/store/units';
import { heightAt } from '@/tides/heights';
import { tidePortNow } from '@/tides/tideNow';
import { publicPlansCountForArea } from '@/pilotage/communityStub';

/** Single-tap show/hide of the whole active plan on the main chart. */
export function PlanVisibilityChip() {
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const planVisible = usePilotagePlannerStore((s) => s.planVisible);
  const setPlanVisible = usePilotagePlannerStore((s) => s.setPlanVisible);
  if (!activePlanId) return null;
  return (
    <button
      type="button"
      className="acp-mapchip"
      aria-pressed={planVisible}
      aria-label={planVisible ? 'Hide the pilotage plan' : 'Show the pilotage plan'}
      onClick={() => setPlanVisible(!planVisible)}
    >
      <span className="ic" aria-hidden="true">{planVisible ? '👁' : '🚫'}</span>{' '}
      {planVisible ? 'Plan' : 'Plan hidden'}
    </button>
  );
}

/** Live height of tide for the reference port, shown while a plan is active. */
export function TideHeightChip() {
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const portId = useEnvStore((s) => s.heightPortId);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const heightSourceId = useTideSourceStore((s) => s.heightSourceId);
  const ms = useEnvStore((s) => s.streamTimeMs);
  const depthUnit = useUnitsStore((s) => s.depth);
  if (!activePlanId) return null;
  // No chip at all where we have no tidal data: an empty or fictional height
  // beside a pilotage plan is worse than no chip.
  const port = tidePortNow(chartProviderId, heightSourceId, portId);
  if (!port) return null;
  const h = heightAt(port, ms);
  if (h == null) return null;
  const prev = heightAt(port, ms - 6 * 60_000);
  const trend = prev == null ? '' : h > prev + 0.005 ? '↑' : h < prev - 0.005 ? '↓' : '→';
  return (
    <div className="acp-mapchip acp-tide-chip" aria-label={`Height of tide at ${port.name}`}>
      <span className="ic" aria-hidden="true">🌊</span> {formatDepth(h, depthUnit)} {trend}
      <span className="sub">{port.name}</span>
    </div>
  );
}

/** Public-plans-count indicator over the plan's chart area (stubbed count). Tap to browse. */
export function CommunityCountChip() {
  const plans = usePilotagePlannerStore((s) => s.plans);
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const openSheet = useShellStore((s) => s.openSheet);
  if (!activePlanId) return null;
  const plan = selectActivePlan({ plans, activePlanId });
  const count = publicPlansCountForArea(plan?.bounds ?? null);
  return (
    <button
      type="button"
      className="acp-mapchip"
      aria-label="Browse community plans shared near here (preview)"
      onClick={() => openSheet('pilotagecommunity')}
    >
      <span className="ic" aria-hidden="true">🌐</span> {count} shared here
    </button>
  );
}

/** Re-enter building/editing for the active plan (shown when a plan is loaded but
 * not currently being built). Gives an obvious way back in after tapping Done. */
export function EditPlanChip() {
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const building = usePilotagePlannerStore((s) => s.building);
  const setBuilding = usePilotagePlannerStore((s) => s.setBuilding);
  const showToast = useShellStore((s) => s.showToast);
  if (!activePlanId || building) return null;
  return (
    <button
      type="button"
      className="acp-mapchip"
      aria-label="Edit this pilotage plan"
      onClick={() => {
        setBuilding(true);
        showToast('Editing plan — the toolbar is back');
      }}
    >
      <span className="ic" aria-hidden="true">✎</span> Edit plan
    </button>
  );
}
